<script setup lang="ts">
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type {
	PropertyTypeColumn,
	RoomOption
} from '~/composables/useRoomTypologies';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { useRoomTypologies } from '~/composables/useRoomTypologies';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';

import TextLink from '../ui/text-link/TextLink.vue';

const props = defineProps<{
	region: string;
	city: string;
	state: string;
	sectionName: SectionName;
	breadcrumbItems?: BreadcrumbItem[];
}>();

const emit = defineEmits<{
	(e: 'breadcrumbClicked', breadcrumbItem: BreadcrumbItemWithIndex): void;
	(e: 'filter-link-clicked', filters: PropertyFilters): void;
}>();

const roomTypologiesData = await useRoomTypologies({
	regions: computed(() => [props.region]),
	city: computed(() => props.city),
	state: computed(() => props.state)
});

const { trackLinkClicked } = useEvents();

const handleLinkClick = (
	link: RoomOption,
	linkCategory: string,
	event: Event
) => {
	trackLinkClicked({
		section_name: props.sectionName,
		destination_url: link.route as string,
		link_text: link.displayLabel,
		link_category: linkCategory,
		current_page: 'Listing Properties'
	});

	const baseFilters: PropertyFilters = {
		regions: [props.region],
		cities: [props.city],
		states: [props.state],
		transactionType: 'sell',
		propertyTypes: [linkCategory as PropertyTypeEnum]
	};

	const finalFilters = link.amount
		? { ...baseFilters, bedrooms: link.amount }
		: baseFilters;

	emit('filter-link-clicked', finalFilters);
};

const handleBreadcrumbClicked = (breadcrumbItem: BreadcrumbItemWithIndex) => {
	emit('breadcrumbClicked', breadcrumbItem);
};

const propertyTypeColumns = computed<PropertyTypeColumn[]>(() => {
	return roomTypologiesData.propertyTypeColumns.value || [];
});

const hasOptions = computed(() => {
	return propertyTypeColumns.value && propertyTypeColumns.value.length > 0;
});
</script>

<template>
	<section v-if="hasOptions" class="flex w-full flex-col">
		<Breadcrumb
			v-if="breadcrumbItems?.length"
			:items="breadcrumbItems"
			@breadcrumb-clicked="handleBreadcrumbClicked"
		/>

		<h2 class="py-6 text-2xl font-medium text-primary md:pt-8 md:text-[32px]">
			Mais opções de imóveis em {{ region }}
		</h2>

		<div class="grid grid-cols-1 gap-8 lg:gap-9 xl:grid-cols-2">
			<div
				v-for="column in propertyTypeColumns"
				:key="column.type"
				class="flex flex-col gap-4 md:gap-6"
			>
				<h3 class="text-xl font-medium text-primary md:text-2xl">
					{{ column.title }}
				</h3>

				<div class="flex flex-col gap-2 md:gap-4">
					<div
						v-for="option in column.roomOptions"
						:key="`${column.type}-${option.roomType}-${option.amount}`"
					>
						<NuxtLink
							:to="option.route"
							@click="handleLinkClick(option, column.type, $event)"
						>
							<TextLink as="p" class="text-primary">
								{{ option.displayLabel }}
							</TextLink>
						</NuxtLink>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
